import React, { useState, useEffect } from 'react';
import { X, ChevronDown, ChevronUp, MessageSquare, FileText, Calendar, User } from 'lucide-react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { format } from 'date-fns';
import { apiService } from '../../services/api';
import { Visit } from '../../types/visit';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { formatUTCDate, formatUTCDateTime, formatDateForBackend } from '../../utils/dateHelpers';

interface FollowUpVisitModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  parentVisit: Visit;
}

export const FollowUpVisitModal: React.FC<FollowUpVisitModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  parentVisit
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showOriginalDetails, setShowOriginalDetails] = useState(false);
  const [formData, setFormData] = useState({
    visitDate: parentVisit.nextVisitDate ? new Date(parentVisit.nextVisitDate) : new Date(),
    status: 'scheduled' as 'scheduled' | 'completed' | 'cancelled' | 'no-show',
    purpose: `Follow-up for visit on ${formatUTCDate(parentVisit.visitDate)}`,
    tags: parentVisit.tags || []
  });

  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      setFormData({
        visitDate: parentVisit.nextVisitDate ? new Date(parentVisit.nextVisitDate) : new Date(),
        status: 'scheduled' as const,
        purpose: `Follow-up for visit on ${formatUTCDate(parentVisit.visitDate)}`,
        tags: parentVisit.tags || []
      });
      setError('');
    }
  }, [isOpen, parentVisit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const visitData = {
        parentVisitId: parentVisit.id,
        provider: parentVisit.provider,
        visitDate: formatDateForBackend(formData.visitDate),
        visitType: 'follow-up',
        status: formData.status,
        purpose: formData.purpose,
        tags: formData.tags
      };

      await apiService.createFollowUpVisit(visitData);
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create follow-up visit');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Schedule Follow-up Visit</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Provider Information (Read-only) */}
            <div className="bg-gray-50 p-4 rounded-md">
              <h3 className="font-medium mb-2">Provider Information</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Name:</span> {parentVisit.provider.name}
                </div>
                <div>
                  <span className="text-gray-600">NPI:</span> {parentVisit.provider.npi}
                </div>
                {parentVisit.provider.specialty && (
                  <div>
                    <span className="text-gray-600">Specialty:</span> {parentVisit.provider.specialty}
                  </div>
                )}
                {parentVisit.provider.address && (
                  <div className="col-span-2">
                    <span className="text-gray-600">Address:</span> {parentVisit.provider.address}
                  </div>
                )}
              </div>
            </div>

            {/* Parent Visit Information (Read-only) */}
            <div className="bg-gray-50 p-4 rounded-md">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-medium">Original Visit Information</h3>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowOriginalDetails(!showOriginalDetails)}
                  className="flex items-center gap-1"
                >
                  {showOriginalDetails ? (
                    <>
                      <ChevronUp className="h-4 w-4" />
                      Hide Details
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4" />
                      Show Details
                    </>
                  )}
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Date:</span> {formatUTCDate(parentVisit.visitDate)}
                </div>
                <div>
                  <span className="text-gray-600">Type:</span> {parentVisit.visitType}
                </div>
              </div>
              
              {showOriginalDetails && (
                <div className="mt-4 pt-4 border-t space-y-4">
                  {/* Purpose */}
                  {parentVisit.purpose && (
                    <div>
                      <h4 className="font-medium text-sm mb-1">Purpose</h4>
                      <p className="text-sm text-gray-600">{parentVisit.purpose}</p>
                    </div>
                  )}
                  
                  {/* Outcome */}
                  {parentVisit.outcome && (
                    <div>
                      <h4 className="font-medium text-sm mb-1">Outcome</h4>
                      <p className="text-sm text-gray-600">{parentVisit.outcome}</p>
                    </div>
                  )}
                  
                  {/* Comments */}
                  {parentVisit.comments && parentVisit.comments.length > 0 && (
                    <div>
                      <h4 className="font-medium text-sm mb-2 flex items-center gap-1">
                        <MessageSquare className="h-4 w-4" />
                        Comments ({parentVisit.comments.length})
                      </h4>
                      <div className="space-y-2">
                        {parentVisit.comments.map((comment: any, index: number) => (
                          <div key={index} className="bg-white p-3 rounded border">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-medium">{comment.user?.name || 'Unknown'}</span>
                              <span className="text-xs text-gray-500">
                                {formatUTCDateTime(comment.createdAt)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700">{comment.text}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Tags */}
                  {parentVisit.tags && parentVisit.tags.length > 0 && (
                    <div>
                      <h4 className="font-medium text-sm mb-1">Tags</h4>
                      <div className="flex flex-wrap gap-1">
                        {parentVisit.tags.map((tag: string, index: number) => (
                          <span key={index} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Status */}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Status:</span> {parentVisit.status}
                    </div>
                    <div>
                      <span className="text-gray-600">Visited by:</span> {parentVisit.visitedBy?.name || 'Unknown'}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Follow-up Visit Details */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Follow-up Visit Date <span className="text-red-500">*</span>
              </label>
              <DatePicker
                selected={formData.visitDate}
                onChange={(date: Date | null) => date && setFormData({ ...formData, visitDate: date })}
                dateFormat="MMM d, yyyy"
                minDate={new Date()}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Status <span className="text-red-500">*</span>
              </label>
              <Select 
                value={formData.status} 
                onValueChange={(value: 'scheduled' | 'completed' | 'cancelled' | 'no-show') => 
                  setFormData({ ...formData, status: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  {/* <SelectItem value="no-show">No Show</SelectItem> */}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Purpose
              </label>
              <textarea
                value={formData.purpose}
                onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Follow-up Visit'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
